import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Cancel, CompareArrows } from '@material-ui/icons';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useQuery } from 'hooks/useQuery';
import { Confluence } from '../interfaces';
import { useMoveArticle } from '../hooks';
import { flatMenu, parseSection, SECTION_LABELS } from '../helpers';
import { loadConfluenceMenu } from '../api';

interface IProps extends Omit<DialogProps, 'onClose'> {
  article: Confluence.IArticle | null;
  menu: Confluence.IMenu;
  onClose(): void;
  onMove?(article: Confluence.IArticle, section: Confluence.ESections): void;
}

const MoveArticleDialog = ({ open, article, menu, onClose, onMove, ...props }: IProps) => {
  const params = useQuery();

  const currentSection = parseSection(params.get('section'));

  const [section, setSection] = useState<Confluence.ESections>(currentSection);
  const [sectionMenu, setSectionMenu] = useState<Confluence.IMenu>(menu || []);

  const { loading, handleMoveArticle } = useMoveArticle(article, (movedArticle) => {
    onClose();
    // целевой раздел отдаём странице: она обновит URL, дерево и саму статью одним проходом
    if (onMove) onMove(movedArticle, section);
  });

  useEffect(() => {
    if (!open) return;

    setSection(currentSection);
  }, [open, currentSection]);

  useEffect(() => {
    if (!open) return;

    if (section === currentSection) {
      setSectionMenu(menu || []);
      return;
    }

    loadConfluenceMenu({ params: { section } }).then(setSectionMenu);
  }, [open, section, currentSection, menu]);

  const initialValues: Confluence.IArticle = {
    parent: null,
  };

  const validationSchema = yup.object().shape({
    parent: yup.object().nullable(),
  });

  return (
    <Dialog {...props} open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="body">
      <DialogTitle>
        <b>Перенести статью</b>
      </DialogTitle>

      <Formik
        onSubmit={(values) => handleMoveArticle(values.parent?.id, section)}
        validationSchema={validationSchema}
        initialValues={initialValues}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    select
                    value={section}
                    variant="filled"
                    label="Раздел"
                    fullWidth
                    disabled={loading}
                    onChange={(event) => {
                      setSection(event.target.value as Confluence.ESections);
                      setFieldValue('parent', null);
                    }}
                  >
                    {Array.from(SECTION_LABELS).map(([value, label]) => (
                      <MenuItem value={value} key={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    value={values.parent}
                    options={flatMenu(sectionMenu || [])}
                    onChange={(event, option) => setFieldValue('parent', option)}
                    disabled={loading}
                    getOptionLabel={(option) => String(option.name)}
                    getOptionSelected={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="parent"
                        variant="filled"
                        label="Родительская статья"
                        placeholder="Выберите из списка"
                        fullWidth
                        helperText={`Если вы не выберете родительскую статью, она будет перенесена в корень раздела «${SECTION_LABELS.get(
                          section,
                        )}». Вложенные статьи переносятся вместе с ней.`}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button variant="contained" disabled={loading} onClick={onClose} startIcon={<Cancel />}>
                Отменить
              </Button>

              <Button
                color="primary"
                variant="contained"
                disabled={loading}
                type="submit"
                startIcon={<CompareArrows />}
              >
                Перенести
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default MoveArticleDialog;
